import { Suspense, lazy } from 'react';
import type { PartialRouteObject } from 'react-router';
import { Navigate } from 'react-router-dom';
import AuthGuard from './components/AuthGuard';
import BlogLayout from './components/blog/BlogLayout';
import FaqLayout from './components/faq/FaqLayout';
import BrowseLayout from './components/BrowseLayout';
import DashboardLayout from './components/dashboard/DashboardLayout';
import DocsLayout from './components/docs/DocsLayout';
import GuestGuard from './components/GuestGuard';
import LoadingScreen from './components/LoadingScreen';
import MainLayout from './components/MainLayout';
// import SettingsDrawer from './components/SettingsDrawer';

/* prettier-ignore */
const Loadable = (Component) => (props) => (
  <Suspense fallback={<LoadingScreen />}>
    <Component {...props} />
  </Suspense>
);

// Browse pages

const Browse = Loadable(lazy(() => import('./pages/browse/Browse')));
const BrowseButtons = Loadable(
  lazy(() => import('./pages/browse/BrowseButtons'))
);
const BrowseCharts = Loadable(
  lazy(() => import('./pages/browse/BrowseCharts'))
);
const BrowseColors = Loadable(
  lazy(() => import('./pages/browse/BrowseColors'))
);
const BrowseDetailLists = Loadable(
  lazy(() => import('./pages/browse/BrowseDetailLists'))
);
const BrowseForms = Loadable(lazy(() => import('./pages/browse/BrowseForms')));
const BrowseGridLists = Loadable(
  lazy(() => import('./pages/browse/BrowseGridLists'))
);
const BrowseGroupedLists = Loadable(
  lazy(() => import('./pages/browse/BrowseGroupedLists'))
);
const BrowseInputs = Loadable(
  lazy(() => import('./pages/browse/BrowseInputs'))
);
const BrowseModals = Loadable(
  lazy(() => import('./pages/browse/BrowseModals'))
);
const BrowseQuickStats = Loadable(
  lazy(() => import('./pages/browse/BrowseQuickStats'))
);
const BrowseTables = Loadable(
  lazy(() => import('./pages/browse/BrowseTables'))
);
const BrowseTypography = Loadable(
  lazy(() => import('./pages/browse/BrowseTypography'))
);

// Authentication pages

const Login = Loadable(lazy(() => import('./pages/authentication/Login')));
const PasswordRecovery = Loadable(
  lazy(() => import('./pages/authentication/PasswordRecovery'))
);
const PasswordUpdateUser = Loadable(
  lazy(() => import('./pages/authentication/PasswordUpdateUser'))
);
const PasswordUpdateProvider = Loadable(
  lazy(() => import('./pages/authentication/PasswordUpdateProvider'))
);
const PasswordReset = Loadable(
  lazy(() => import('./pages/authentication/PasswordReset'))
);
const Register = Loadable(
  lazy(() => import('./pages/authentication/Register'))
);
const VerifyCode = Loadable(
  lazy(() => import('./pages/authentication/VerifyCode'))
);

// Blog pages

const BlogPostCreate = Loadable(
  lazy(() => import('./pages/blog/BlogPostCreate'))
);
const BlogPostDetails = Loadable(
  lazy(() => import('./pages/blog/BlogPostDetails'))
);
const BlogPostList = Loadable(lazy(() => import('./pages/blog/BlogPostList')));

// Faq page

const FaqList = Loadable(lazy(() => import('./pages/faq/FaqList')));

// Dashboard pages

const Account = Loadable(lazy(() => import('./pages/dashboard/Account')));
const Analytics = Loadable(lazy(() => import('./pages/dashboard/Analytics')));
const Contract = Loadable(lazy(() => import('./pages/dashboard/Contract')));
const Business = Loadable(lazy(() => import('./pages/dashboard/BusinessList')));
const BusinessDetails = Loadable(
  lazy(() => import('./pages/dashboard/BusinessDetails'))
);
const BusinessProviders = Loadable(
  lazy(() => import('./pages/dashboard/BusinessProviders'))
);
const Calendar = Loadable(lazy(() => import('./pages/dashboard/Calendar')));
const Chat = Loadable(lazy(() => import('./pages/dashboard/Chat')));
const CustomerDetails = Loadable(
  lazy(() => import('./pages/dashboard/CustomerDetails'))
);
const CustomerEdit = Loadable(
  lazy(() => import('./pages/dashboard/CustomerEdit'))
);
const CustomerList = Loadable(
  lazy(() => import('./pages/dashboard/CustomerList'))
);
const UserList = Loadable(lazy(() => import('./pages/dashboard/UserList')));
const Finance = Loadable(lazy(() => import('./pages/dashboard/Finance')));
const InvoiceDetails = Loadable(
  lazy(() => import('./pages/dashboard/InvoiceDetails'))
);
const InvoiceList = Loadable(
  lazy(() => import('./pages/dashboard/InvoiceList'))
);
const Glossary = Loadable(lazy(() => import('./pages/dashboard/Glossary')));
const Kanban = Loadable(lazy(() => import('./pages/dashboard/Kanban')));
const Mail = Loadable(lazy(() => import('./pages/dashboard/Mail')));
const OrderDetails = Loadable(
  lazy(() => import('./pages/dashboard/OrderDetails'))
);
const OrderList = Loadable(lazy(() => import('./pages/dashboard/OrderList')));
const Overview = Loadable(lazy(() => import('./pages/dashboard/Overview')));
const ProductCreate = Loadable(
  lazy(() => import('./pages/dashboard/ProductCreate'))
);
const ProductList = Loadable(
  lazy(() => import('./pages/dashboard/ProductList'))
);
// const UploadFile = Loadable(lazy(() => import('./pages/dashboard/UploadFile')));
const ProvidersList = Loadable(
  lazy(() => import('./pages/dashboard/ProvidersList'))
);
const Transactions = Loadable(
  lazy(() => import('./pages/dashboard/Transactions'))
);
const UserMasterConfig = Loadable(
  lazy(() => import('./pages/dashboard/UserMasterConfig'))
);
const Settings = Loadable(lazy(() => import('./pages/dashboard/Settings')));
const Help = Loadable(lazy(() => import('./pages/dashboard/HelpSection')));

// Docs pages

const Docs = Loadable(lazy(() => import('./pages/Docs')));

// Error pages

const AuthorizationRequired = Loadable(
  lazy(() => import('./pages/AuthorizationRequired'))
);
const NotFound = Loadable(lazy(() => import('./pages/NotFound')));
const ServerError = Loadable(lazy(() => import('./pages/ServerError')));

// Projects pages

const ProjectBrowse = Loadable(
  lazy(() => import('./pages/dashboard/ProjectBrowse'))
);
const ProjectCreate = Loadable(
  lazy(() => import('./pages/dashboard/ProjectCreate'))
);
const ProjectDetails = Loadable(
  lazy(() => import('./pages/dashboard/ProjectDetails'))
);

// Social pages

const SocialFeed = Loadable(lazy(() => import('./pages/dashboard/SocialFeed')));
const SocialProfile = Loadable(
  lazy(() => import('./pages/dashboard/SocialProfile'))
);

// Other pages

const Checkout = Loadable(lazy(() => import('./pages/Checkout')));
const Contact = Loadable(lazy(() => import('./pages/Contact')));
const Home = Loadable(lazy(() => import('./pages/Home')));
const HomeProveedor = Loadable(lazy(() => import('./pages/HomeProveedor')));
const Politics = Loadable(lazy(() => import('./pages/Politics')));
const Comenzar = Loadable(lazy(() => import('./pages/Comenzar')));
const Conditions = Loadable(lazy(() => import('./pages/Conditions')));
const Pricing = Loadable(lazy(() => import('./pages/Pricing')));
const ContractView = Loadable(lazy(() => import('./pages/Contract')));

const routes: PartialRouteObject[] = [
  { path: '/contract', element: <ContractView /> },
  {
    path: '/reset',
    children: [
      {
        path: ':resetToken',
        element: <PasswordRecovery />,
      },
    ],
  },
  {
    path: '/update-password',
    children: [
      {
        path: ':updateToken',
        element: <PasswordUpdateProvider />,
      },
    ],
  },
  {
    path: '/update-password-user',
    children: [
      {
        path: ':updateToken',
        element: <PasswordUpdateUser />,
      },
    ],
  },
  {
    path: 'authentication',
    children: [
      {
        path: 'login',
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        ),
      },
      {
        path: 'login-unguarded',
        element: <Login />,
      },
      {
        path: 'password-reset',
        element: <PasswordReset />,
      },
      {
        path: 'register',
        element: (
          <GuestGuard>
            <Register />
          </GuestGuard>
        ),
      },
      {
        path: 'register-unguarded',
        element: <Register />,
      },
      {
        path: 'verify-code',
        element: <VerifyCode />,
      },
    ],
  },
  {
    path: 'blog',
    element: <BlogLayout />,
    children: [
      {
        path: '/',
        element: <BlogPostList />,
      },
      {
        path: 'new',
        element: <BlogPostCreate />,
      },
      {
        path: ':postId',
        element: <BlogPostDetails />,
      },
    ],
  },
  {
    path: 'faq',
    element: <FaqLayout />,
    children: [
      {
        path: '/',
        element: <FaqList />,
      },
    ],
  },
  {
    path: 'contact',
    element: <Contact />,
  },
  {
    path: 'dashboard',
    element: (
      <AuthGuard>
        <DashboardLayout />
        {/* <SettingsDrawer /> */}
      </AuthGuard>
    ),
    children: [
      {
        path: '/',
        element: <Overview />,
      },
      {
        path: 'account',
        element: <Account />,
      },
      {
        path: 'glossary',
        element: <Glossary />,
      },
      {
        path: 'settings',
        element: <Settings />,
      },
      {
        path: 'users',
        element: <UserMasterConfig />,
      },
      {
        path: 'help',
        element: <Help />,
      },
      {
        path: 'analytics',
        element: <Analytics />,
      },
      {
        path: 'contract',
        element: <Contract />,
      },
      {
        path: 'calendar',
        element: <Calendar />,
      },
      {
        path: 'chat',
        children: [
          {
            path: '/',
            element: <Chat />,
          },
          {
            path: 'new',
            element: <Chat />,
          },
          {
            path: ':threadKey',
            element: <Chat />,
          },
        ],
      },
      {
        path: 'users',
        children: [
          {
            path: ':businessId',
            element: <UserList />,
          },
        ],
      },
      {
        path: 'providers',
        children: [
          {
            path: '/',
            element: <ProvidersList />,
          },
        ],
      },
      {
        path: 'transactions',
        children: [
          {
            path: '/',
            element: <Transactions />,
          },
        ],
      },
      {
        path: 'customers',
        children: [
          {
            path: '/',
            element: <CustomerList />,
          },
          {
            path: ':customerId',
            element: <CustomerDetails />,
          },
          {
            path: ':customerId/edit',
            element: <CustomerEdit />,
          },
        ],
      },
      {
        path: 'invoices',
        children: [
          {
            path: '/',
            element: <InvoiceList />,
          },
          {
            path: ':invoiceId',
            element: <InvoiceDetails />,
          },
        ],
      },
      {
        path: 'kanban',
        element: <Kanban />,
      },
      {
        path: 'mail',
        children: [
          {
            path: '/',
            element: <Navigate to="/dashboard/mail/all" replace />,
          },
          {
            path: 'label/:customLabel',
            element: <Mail />,
          },
          {
            path: 'label/:customLabel/:emailId',
            element: <Mail />,
          },
          {
            path: ':systemLabel',
            element: <Mail />,
          },
          {
            path: ':systemLabel/:emailId',
            element: <Mail />,
          },
        ],
      },
      {
        path: 'orders',
        children: [
          {
            path: '/',
            element: <OrderList />,
          },
          {
            path: ':orderId',
            element: <OrderDetails />,
          },
        ],
      },
      {
        path: 'business',
        children: [
          {
            path: '/',
            element: <Business />,
          },
          {
            path: ':businessId',
            element: <BusinessDetails />,
          },
          {
            path: 'config',
            children: [
              {
                path: ':businessId',
                element: <BusinessProviders />,
              },
            ],
          },
        ],
      },
      {
        path: 'finance',
        element: <Finance />,
      },
      {
        path: 'products',
        children: [
          {
            path: '/',
            element: <ProductList />,
          },
          {
            path: 'new',
            element: <ProductCreate />,
          },
        ],
      },
      {
        path: 'projects',
        children: [
          {
            path: 'browse',
            element: <ProjectBrowse />,
          },
          {
            path: 'new',
            element: <ProjectCreate />,
          },
          {
            path: ':projectId',
            element: <ProjectDetails />,
          },
        ],
      },
      {
        path: 'social',
        children: [
          {
            path: 'feed',
            element: <SocialFeed />,
          },
          {
            path: 'profile',
            element: <SocialProfile />,
          },
        ],
      },
    ],
  },
  {
    path: 'docs',
    element: <DocsLayout />,
    children: [
      {
        path: '/',
        element: <Navigate to="/docs/overview/welcome" replace />,
      },
      {
        path: '*',
        element: <Docs />,
      },
    ],
  },
  {
    path: '*',
    element: <MainLayout />,
    children: [
      {
        path: '/',
        element: <Home />,
      },
      {
        path: '/proveedor',
        element: <HomeProveedor />,
      },
      {
        path: '/condiciones',
        element: <Conditions />,
      },
      {
        path: '/politicas',
        element: <Politics />,
      },
      {
        path: '/comenzar',
        element: <Comenzar />,
      },
      //   children: [
      //     {
      //       path: 'landing',
      //       children: [
      //         {
      //           path: ':proveedor',
      //           element: <Home />,
      //         },
      //       ],
      //     },
      //   ],
      // },
      {
        path: 'browse',
        element: <BrowseLayout />,
        children: [
          {
            path: '/',
            element: <Browse />,
          },
          {
            path: '/buttons',
            element: <BrowseButtons />,
          },
          {
            path: '/inputs',
            element: <BrowseInputs />,
          },
          {
            path: '/charts',
            element: <BrowseCharts />,
          },
          {
            path: '/colors',
            element: <BrowseColors />,
          },
          {
            path: '/data-display/detail-lists',
            element: <BrowseDetailLists />,
          },
          {
            path: '/data-display/quick-stats',
            element: <BrowseQuickStats />,
          },
          {
            path: '/data-display/tables',
            element: <BrowseTables />,
          },
          {
            path: '/forms',
            element: <BrowseForms />,
          },
          {
            path: '/modals',
            element: <BrowseModals />,
          },
          {
            path: '/lists/grouped-lists',
            element: <BrowseGroupedLists />,
          },
          {
            path: '/lists/grid-lists',
            element: <BrowseGridLists />,
          },
          {
            path: '/typography',
            element: <BrowseTypography />,
          },
        ],
      },
      {
        path: 'checkout',
        element: <Checkout />,
      },
      {
        path: 'pricing',
        element: <Pricing />,
      },
      {
        path: '401',
        element: <AuthorizationRequired />,
      },
      {
        path: '404',
        element: <NotFound />,
      },
      {
        path: '500',
        element: <ServerError />,
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
];

export default routes;
